import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import "../styles/auth.css";

const functions = getFunctions();
const sendPasswordResetEmail = httpsCallable(
  functions,
  "sendPasswordResetEmail",
);

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!navigator.onLine) {
      setError(
        "Password reset requires an internet connection. Please check your network and try again.",
      );
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail({ email });
      setIsSubmitted(true);
    } catch (error: any) {
      console.error("Password reset error:", error);

      if (error.code === "functions/invalid-argument") {
        setError("Please enter a valid email address.");
      } else if (error.code === "functions/internal") {
        setError(
          "An error occurred while sending the reset email. Please try again.",
        );
      } else {
        setError(
          "An error occurred while sending the reset email. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="w-full bg-base-100">
        <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen bg-base-100">
          <div className="p-8 w-full mx-auto text-center">
            <h1 className="text-2xl font-semibold text-base-content mb-4">
              Check Your Email
            </h1>
            <p className="text-base-content/70 mb-6">
              If an account exists for {email}, we've sent a password reset link to it.
            </p>
            <a
              href="/login"
              className="text-primary hover:text-primary-dark font-medium"
            >
              Back to Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-base-100">
      <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
        <div className="p-8 w-full mx-auto">
          <h1 className="text-2xl font-semibold text-center text-base-content mb-2">
            Forgot Password
          </h1>
          <p className="text-center mb-6 text-base-content/70">
            Enter your email address and we'll send you a link to reset your
            password
          </p>

          {error && (
            <div className="alert alert-error mb-4">
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 w-full">
            <div className="form-group w-full">
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder="Enter your email"
                />
                <label htmlFor="email">Email</label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-base-100 mr-2"></div>
                  Sending reset link...
                </div>
              ) : (
                "Send reset link"
              )}
            </button>

            <div className="text-center pt-4">
              <p className="text-sm text-base-content/70">
                Remember your password?{" "}
                <a
                  href="/login"
                  className="text-base-content hover:text-base-content/80 font-medium"
                >
                  Back to Sign In
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
